import type { CommandGroupType } from "@layer/utils/lib/commands";
import { atom, type Getter, type Setter } from "jotai";
import type { useRouter } from "next/navigation";
import { setCommandOpenAtom } from "@/modules/command-bar/atoms/states/command-open-state";
import { setSelectCommandPageAtom } from "@/modules/command-bar/atoms/states/command-page-state";

/**
 * Handles the execution of a command based on the command object
 *
 * @param {CommandItemType} command - The command object that contains the id, href
 * @param {ReturnType<typeof useRouter>} [router] - Optional router instance for handling navigation.
 */
const handleCommandAction = (
	command: CommandGroupType,
	router?: ReturnType<typeof useRouter>,
) => {
	const { href } = command;
	if (router && href) {
		router.push(href);
	}
};

/**
 * A custom set atom for handling command actions based on the current command and state.
 *
 * @param {Getter} - get
 * @param {Setter} - set
 * @param {command} - The command object that contains the id, href etc other relevant properties.
 * @param {ReturnType<typeof useRouter>} [params.router] - Optional router instance for handling navigation.
 */
export const setCommandActionAtom = atom(
	null,
	(
		_get: Getter,
		set: Setter,
		{
			command,
			router,
		}: { command: CommandGroupType; router?: ReturnType<typeof useRouter> },
	) => {
		const { id, items, custom } = command;
		if (items || custom) {
			set(setSelectCommandPageAtom, id);
			set(setCommandOpenAtom, true);
		} else {
			set(setCommandOpenAtom, false);
			handleCommandAction(command, router);
		}
	},
);
